/**
 * MobileFAB — Standardized Floating Action Button for the unified mobile layout.
 *
 * Positioned to avoid collision with MobileBottomToolbar (72px) and safe-area insets.
 * Uses haptic feedback, scale animation, and accessible labeling.
 *
 * Position: fixed bottom-24 right-4 (above toolbar + safe area)
 *
 * Usage:
 * ```tsx
 * <MobileFAB icon={Plus} label="New Contact" onPress={() => setOpen(true)} />
 * ```
 */

import React from 'react';
import { type LucideIcon } from 'lucide-react';
import { useHaptic } from '@shared/hooks/useHaptic';

export interface MobileFABProps {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Accessible label (also used for extended mode text) */
  label?: string;
  /** Press handler */
  onPress: () => void;
  /** Visual variant */
  variant?: 'primary' | 'secondary';
  /** Show label text next to icon (pill shape) */
  extended?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

export const MobileFAB: React.FC<MobileFABProps> = ({
  icon: Icon,
  label,
  onPress,
  variant = 'primary',
  extended = false,
  disabled = false,
  className = '',
}) => {
  const haptic = useHaptic();

  const handlePress = () => {
    if (disabled) return;
    haptic.light();
    onPress();
  };

  const variants = {
    primary: 'bg-brand-600 text-white shadow-lg shadow-brand-900/30 active:bg-brand-700',
    secondary: 'bg-dark-surface/95 text-dark-text border border-dark-border/30 shadow-lg shadow-black/30 active:bg-dark-surface',
  };

  return (
    <button
      type="button"
      onClick={handlePress}
      disabled={disabled}
      aria-label={label || 'Action'}
      className={`
        fixed bottom-24 right-4 z-40
        md:hidden
        ${extended
          ? 'px-5 py-3 rounded-2xl flex items-center gap-2.5'
          : 'min-h-[56px] min-w-[56px] rounded-full flex items-center justify-center'
        }
        ${variants[variant]}
        backdrop-blur-sm
        transition-all duration-200
        active:scale-95
        focus-visible:ring-4 focus-visible:ring-brand-500/30 focus-visible:outline-none
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      <Icon className="w-6 h-6" />
      {extended && label && (
        <span className="font-semibold text-sm whitespace-nowrap">{label}</span>
      )}
    </button>
  );
};
